import React, { useState } from 'react';
import DashboardCards from './DashboardCards';
import WorkersTable from './WorkersTable';
import RustyKaspaTable from './RustyKaspaTable';
import ZkasMinerCard from './ZkasMinerCard';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from 'recharts';

const DetailsTab = ({
  kasPriceHistory,
  pricePeriod,
  setPricePeriod,
  networkHashrateHistory,
  period,
  setPeriod,
  earnings,
  hashrates,
  activeWorkers,
  workers,
  userData,
  workerFinance,
  totalColor,
  isMobile,
  styles,
  formatNumber,
  formatDateTime,
  formatDate,
  balanceHistory,
  balanceHistoryLoading,
  balanceHistoryError,
  kasAddedHistory: kasAddedTransactionHistory,
  kasAddedLoading,
  kasAddedError,
  poolData,
  k1PoolData,
  kasPrice,
  priceLoading,
  priceError,
  miningTotal,
  miningBalanceLoading,
  miningBalanceError,
  ledgerTotal,
  getExpectedHashrate,
  timeUntilRefresh,
  formatTime,
  kasChange24h,
  kasHigh24h,
  kasLow24h,
  kaspaData,
  kaspaError,
  kaspaFetchTime,
  usdcMiningBalance
}) => {
  const [kasBalancePeriod, setKasBalancePeriod] = useState('month');
  const [zkasFinds, setZkasFinds] = useState(null);

  // Group payments by date and sum KAS/USDC for each date
  const getGroupedPayments = () => {
    if (!userData?.payments || userData.payments.length === 0) return [];
    const groupedByDate = {};
    // Process ALL payments, not just first 30
    userData.payments.forEach(p => {
      const d = new Date(p.date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = d.toLocaleString('en-GB', { month: 'short' });
      const year = d.getFullYear();
      const dateKey = `${day}-${month}-${year}`;
      
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = { date: `${day}-${month}`, sortDate: d, kas: 0, usdc: 0 };
      }
      
      if (p.ticker === 'KAS') {
        groupedByDate[dateKey].kas += Number(p.value) || 0;
      } else if (p.ticker === 'USDC') {
        groupedByDate[dateKey].usdc += Number(p.value) || 0;
      }
    });
    
    // Sort by actual date and return last 30 entries
    return Object.values(groupedByDate)
      .sort((a, b) => a.sortDate - b.sortDate)
      .slice(-30)
      .map(({ sortDate, ...rest }) => rest);
  };

  const kasBalancePeriodDays = {
    day: 1,
    week: 7,
    month: 30,
    year: 365,
  }[kasBalancePeriod];
  const kasPeriodHistory = balanceHistory.slice(-kasBalancePeriodDays);

  return (
  <div>
    <RustyKaspaTable
      kaspaData={kaspaData}
      error={kaspaError}
      kaspaFetchTime={kaspaFetchTime}
      showSummary={true}
      showWorkers={false}
      zkasFinds={zkasFinds}
    />
    {/* Dashboard Stat Cards */}
    <DashboardCards
      userData={userData}
      poolData={poolData}
      k1PoolData={k1PoolData}
      balances={userData && userData.balances ? userData.balances.filter(b => b.balance > 0) : []}
      earnings={earnings}
      hashrates={hashrates}
      activeWorkers={activeWorkers}
      workers={workers}
      workerFinance={workerFinance}
      totalColor={totalColor}
      isMobile={isMobile}
      kasPrice={kasPrice}
      priceLoading={priceLoading}
      priceError={priceError}
      miningTotal={miningTotal}
      miningBalanceLoading={miningBalanceLoading}
      miningBalanceError={miningBalanceError}
      ledgerTotal={ledgerTotal}
      styles={styles}
      formatNumber={formatNumber}
      getExpectedHashrate={getExpectedHashrate}
      timeUntilRefresh={timeUntilRefresh}
      formatTime={formatTime}
      kasChange24h={kasChange24h}
      usdcMiningBalance={usdcMiningBalance}
    />

    <RustyKaspaTable
      kaspaData={kaspaData}
      error={kaspaError}
      kaspaFetchTime={kaspaFetchTime}
      showSummary={false}
      showWorkers={true}
      isDetailsTab={true}
      onZkasFindsChange={setZkasFinds}
    />
    <ZkasMinerCard
      styles={styles}
      isMobile={isMobile}
      getExpectedHashrate={getExpectedHashrate}
      isDetailsTab={true}
    />
    <WorkersTable
      userData={userData}
      workers={workers}
      isMobile={isMobile}
      styles={styles}
      formatNumber={formatNumber}
      getExpectedHashrate={getExpectedHashrate}
      isDetailsTab={true}
    />
    {/* Kaspa Price History */}
    {kasPriceHistory.length > 0 && (
      <div className="box mt-5" style={styles.chartBox}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
          <h2 className="title is-5" style={styles.tableTitle}>Kaspa Price History</h2>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <select value={pricePeriod} onChange={e => setPricePeriod(e.target.value)} style={{marginRight: '1rem', backgroundColor: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '4px', padding: '0.5rem'}}>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <span style={{color: '#94a3b8', fontSize: '0.7em', fontWeight: 400, marginBottom: 2}}>
                High: {kasHigh24h !== null && kasHigh24h !== undefined ? `$${kasHigh24h.toFixed(4)}` : '—'}
              </span>
              <span style={{color: '#94a3b8', fontSize: '1.2em', fontWeight: 'bold'}}>
                {kasPrice !== null && kasPrice !== undefined ? `$${kasPrice.toFixed(4)}` : '—'}
              </span>
              <span style={{color: '#94a3b8', fontSize: '0.7em', fontWeight: 400, marginTop: 2}}>
                Low: {kasLow24h !== null && kasLow24h !== undefined ? `$${kasLow24h.toFixed(4)}` : '—'}
              </span>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={kasPriceHistory}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis 
              dataKey="timestamp" 
              stroke="#94a3b8"
              interval={Math.max(1, Math.floor(kasPriceHistory.length / (pricePeriod === 'day' ? 6 : pricePeriod === 'week' ? 7 : pricePeriod === 'month' ? 10 : 12)))}
              tickFormatter={
                pricePeriod === 'day'
                  ? (value) => new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                  : pricePeriod === 'week'
                  ? (value) => new Date(value).toLocaleDateString('en-GB', { month: 'short', day: '2-digit' })
                  : pricePeriod === 'month'
                  ? (value) => new Date(value).toLocaleDateString('en-GB', { month: 'short', day: '2-digit' })
                  : (value) => new Date(value).toLocaleDateString('en-GB', { month: 'short' })
              }
            />
            <YAxis 
              stroke="#94a3b8"
              tickFormatter={value => `$${value.toFixed(4)}`}
              axisLine={true}
              domain={['dataMin', 'dataMax']}
            />
            <Tooltip
              contentStyle={{backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0'}}
              labelStyle={{ color: '#94a3b8' }}
              formatter={value => [`$${value.toFixed(4)}`, 'Price']}
              labelFormatter={value => new Date(value).toLocaleString()}
            />
            <Line
              type="monotone"
              dataKey="price"
              stroke="#22c55e"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#16a34a' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}

    {/* Network Hashrate History */}
    {networkHashrateHistory.length > 0 && (
      <div className="box mt-5" style={styles.chartBox}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
          <h2 className="title is-5" style={styles.tableTitle}>Network Hashrate History</h2>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <select value={period} onChange={e => setPeriod(e.target.value)} style={{marginRight: '1rem', backgroundColor: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '4px', padding: '0.5rem'}}>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
            <span style={{color: '#94a3b8', fontSize: '1.2em', fontWeight: 'bold'}}>
              {(networkHashrateHistory[networkHashrateHistory.length - 1]?.hashrate / 1e15).toFixed(0)} PH/s
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={networkHashrateHistory}
            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis 
              dataKey="timestamp" 
              stroke="#94a3b8"
              interval={Math.max(1, Math.floor(networkHashrateHistory.length / (period === 'day' ? 6 : period === 'week' ? 7 : period === 'month' ? 10 : 12)))}
              tickFormatter={
                period === 'day'
                  ? (value) => new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                  : period === 'week'
                  ? (value) => new Date(value).toLocaleDateString('en-GB', { month: 'short', day: '2-digit' })
                  : period === 'month'
                  ? (value) => new Date(value).toLocaleDateString('en-GB', { month: 'short', day: '2-digit' })
                  : (value) => new Date(value).toLocaleDateString('en-GB', { month: 'short' })
              }
            />
            <YAxis 
              stroke="#94a3b8"
              tick={true}
              axisLine={true}
              tickFormatter={value => `${(value / 1e15).toFixed(0)}`}
              domain={['dataMin', 'dataMax']}
            />
            <Tooltip
              contentStyle={{backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0'}}
              labelStyle={{ color: '#94a3b8' }}
              formatter={value => [`${(value / 1e15).toFixed(0)} PH/s`, 'Network Hashrate']}
              labelFormatter={value => new Date(value).toLocaleString()}
            />
            <Line
              type="monotone"
              dataKey="hashrate"
              stroke="#22c55e"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#16a34a' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}

    {/* Pool Hashrate History */}
    {earnings.length > 0 && (
      <div className="box mt-5" style={styles.chartBox}>
        <h2 className="title is-5 mb-4" style={styles.tableTitle}>Powerpool Hashrate History</h2>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={earnings.slice(0, 14).reverse().map(e => {
              const dateObj = new Date(e.earning_timestamp);
              const day = String(dateObj.getDate()).padStart(2, '0');
              const month = dateObj.toLocaleString('en-GB', { month: 'short' });
              return {
                date: `${day}-${month}`,
                hashrate: Math.round(e.speed),
                usd: Math.round(e.usd_value)
              };
            })}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis 
              dataKey="date" 
              stroke="#94a3b8"
              tickFormatter={value => value}
            />
            <YAxis stroke="#94a3b8" tickFormatter={value => `${value} TH`} />
            <Tooltip
              contentStyle={{backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0'}}
              labelStyle={{ color: '#94a3b8' }}
              formatter={(value, name) => [
                name === 'hashrate' ? `${value} TH/s` : `$${value}`,
                name === 'hashrate' ? 'Hashrate' : 'USD'
              ]}
            />
            <Line
              type="monotone"
              dataKey="hashrate"
              stroke="#60a5fa"
              strokeWidth={2}
              dot={{ fill: '#60a5fa', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: '#3b82f6' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}

    {/* Payments Bar Chart */}
    {userData?.payments && userData.payments.length > 0 && (
      <div className="box mt-5" style={styles.chartBox}>
        <h2 className="title is-5 mb-4" style={styles.tableTitle}>Recent Payments</h2>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={getGroupedPayments()}
            margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="date" stroke="#94a3b8" tickFormatter={value => value} />
            <YAxis stroke="#22c55e" tickFormatter={value => `${value} KAS`} yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" stroke="#60a5fa" tickFormatter={value => `${value} USDC`} />
            <Tooltip
              contentStyle={{backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0'}}
              labelStyle={{ color: '#94a3b8' }}
              formatter={(value, name) => {
                if (name === 'kas') return [`${Math.round(value)} KAS`, 'KAS Payment'];
                if (name === 'usdc') return [`${Math.round(value)} USDC`, 'USDC Payment'];
                return [value, name];
              }}
              labelFormatter={label => label}
            />
            <Bar dataKey="kas" fill="#22c55e" yAxisId="left" />
            <Bar dataKey="usdc" fill="#60a5fa" yAxisId="right" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}

    {/* Recent Payments Table */}
    {userData?.payments && userData.payments.length > 0 && (
      <div className="box mt-5" style={styles.tableBox}>
        <h2 className="title is-5 mb-4" style={styles.tableTitle}>Recent Payments</h2>
        <div className="table-container">
          <table className="table is-fullwidth is-hoverable" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Date</th>
                <th style={styles.tableHeader}>Amount</th>
                <th style={styles.tableHeader}>TX ID</th>
              </tr>
            </thead>
            <tbody>
              {userData.payments
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 14)
                .map((payment, index) => (
                <tr key={index}>
                  <td style={styles.tableCell}>{formatDateTime(payment.date)}</td>
                  <td style={styles.tableCell}>{formatNumber(payment.value, 0)} {payment.ticker}</td>
                  <td style={styles.tableCell}>
                    {payment.txid ? (
                      payment.ticker === 'KAS' ? (
                        <a href={`https://explorer.kaspa.org/txs/${payment.txid}`} target="_blank" rel="noopener noreferrer" style={styles.txLink} title={payment.txid}>
                          {isMobile ? `${payment.txid.substring(0, 8)}...` : `${payment.txid.substring(0, 16)}...`}
                        </a>
                      ) : (
                        <a href={`https://etherscan.io/tx/${payment.txid}`} target="_blank" rel="noopener noreferrer" style={styles.txLink} title={payment.txid}>
                          {isMobile ? `${payment.txid.substring(0, 8)}...` : `${payment.txid.substring(0, 16)}...`}
                        </a>
                      )
                    ) : 'Pending'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {/* Earnings Chart */}
    {earnings.length > 0 && (
      <div className="box mt-5" style={styles.chartBox}>
        <h2 className="title is-5 mb-4" style={styles.tableTitle}>Earnings History</h2>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={earnings.slice(0, 14).reverse().map(e => {
              // Parse date as YYYY-MM-DD
              const dateObj = new Date(e.earning_timestamp);
              const day = String(dateObj.getDate()).padStart(2, '0');
              const month = dateObj.toLocaleString('en-GB', { month: 'short' });
              const formattedDate = `${day}-${month}`;
              const kasEarning = e.coins?.find(c => c.coin_ticker === 'KAS');
              const usdcEarning = e.coins?.find(c => c.coin_ticker === 'USDC');
              return {
                date: formattedDate,
                earnings: Math.round(kasEarning?.coin_balance || 0),
                usdc: usdcEarning ? Number(usdcEarning.coin_balance) : 0
              };
            })}
            margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="date" stroke="#94a3b8"
              tickFormatter={value => value}
            />
            <YAxis 
              stroke="#22c55e"
              tickFormatter={value => `${value} KAS`} 
              yAxisId="left"
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#60a5fa"
              tickFormatter={value => `${value} USDC`}
            />
            <Tooltip
              contentStyle={{backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0'}}
              labelStyle={{ color: '#94a3b8' }}
              formatter={(value, name) => {
                if (name === 'earnings') return [`${value} KAS`, 'KAS Earnings'];
                if (name === 'usdc') return [`${value} USDC`, 'USDC Earnings'];
                return [value, name];
              }}
              labelFormatter={value => value}
            />
            <Line
              type="monotone"
              dataKey="earnings"
              stroke="#22c55e"
              strokeWidth={2}
              dot={{ fill: '#22c55e', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: '#16a34a' }}
              yAxisId="left"
            />
            <Line
              type="monotone"
              dataKey="usdc"
              stroke="#60a5fa"
              strokeWidth={2}
              dot={{ fill: '#60a5fa', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: '#2563eb' }}
              yAxisId="right"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}

    {/* Earnings Table */}
    {earnings.length > 0 && (
      <div className="box mt-5" style={styles.tableBox}>
        <h2 className="title is-5 mb-4" style={styles.tableTitle}>Recent Earnings</h2>
        <div className="table-container">
          <table className="table is-fullwidth is-hoverable" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Date</th>
                <th style={styles.tableHeader}>Earnings</th>
                <th style={styles.tableHeader}>USD Value</th>
                {!isMobile && <th style={styles.tableHeader}>Hashrate</th>}
              </tr>
            </thead>
            <tbody>
              {earnings.slice(0, 14).map((earning, index) => (
                <tr key={index}>
                  <td style={styles.tableCell}>{formatDate(earning.earning_timestamp)}</td>
                  <td style={styles.tableCell}>{earning.coins?.map((coin, i) => (<div key={i}>{formatNumber(coin.coin_balance, 0)} {coin.coin_ticker}</div>))}</td>
                  <td style={styles.tableCell}>${formatNumber(earning.usd_value, 0)}</td>
                  {!isMobile && <td style={styles.tableCell}>{formatNumber(earning.speed, 0)} {earning.speed_units}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {balanceHistoryLoading && (
      <div role="status" style={{ color: '#94a3b8', marginTop: 20 }}>Loading mining wallet history...</div>
    )}
    {balanceHistoryError && (
      <div role="alert" className="box mt-5" style={{ color: '#fbbf24', background: '#292313', border: '1px solid #854d0e' }}>
        {balanceHistoryError}
      </div>
    )}
    {!balanceHistoryLoading && !balanceHistoryError && balanceHistory.length === 0 && (
      <div role="status" style={{ color: '#94a3b8', marginTop: 20 }}>Mining wallet history is not available yet.</div>
    )}

    {/* Mining Wallet Balance History (KAS) */}
    {balanceHistory.length > 0 && (
      <div className="box mt-5" style={styles.chartBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h2 className="title is-5 mb-4" style={styles.tableTitle}>Mining Wallet Balance History (KAS)</h2>
          <select
            value={kasBalancePeriod}
            onChange={event => setKasBalancePeriod(event.target.value)}
            style={{ marginBottom: '1rem', backgroundColor: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '4px', padding: '0.5rem' }}
          >
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={kasPeriodHistory.map(item => {
              const d = new Date(item.date);
              const day = String(d.getDate()).padStart(2, '0');
              const month = d.toLocaleString('en-GB', { month: 'short' });
              return {
                ...item,
                dateLabel: `${day}-${month}`
              };
            })}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="dateLabel" stroke="#94a3b8" tickFormatter={value => value} />
            <YAxis stroke="#94a3b8" tickFormatter={value => `${value} KAS`} />
            <Tooltip
              contentStyle={{backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0'}}
              labelStyle={{ color: '#94a3b8' }}
              formatter={value => [`${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })} KAS`, 'Balance']}
              labelFormatter={label => label}
            />
            <Line
              type="monotone"
              dataKey="balance"
              stroke="#60a5fa"
              strokeWidth={1}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}

    {/* KAS received in confirmed transactions */}
    {(kasAddedLoading || kasAddedError || kasAddedTransactionHistory.length > 0) && (
      <div className="box mt-5" style={styles.chartBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h2 className="title is-5 mb-4" style={styles.tableTitle}>KAS Added to Mining Wallet</h2>
          <span style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1rem' }}>Past 14 days</span>
        </div>
        {kasAddedLoading && <div role="status" style={{ color: '#94a3b8', marginBottom: 12 }}>Loading confirmed transactions...</div>}
        {kasAddedError && <div role="alert" style={{ color: '#fbbf24', marginBottom: 12 }}>{kasAddedError}</div>}
        {kasAddedTransactionHistory.length > 0 && (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={kasAddedTransactionHistory.map(item => {
            const date = new Date(`${item.date}T12:00:00`);
            return {
              dateLabel: `${String(date.getDate()).padStart(2, '0')}-${date.toLocaleString('en-GB', { month: 'short' })}`,
              added: item.added,
            };
          })} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="dateLabel" stroke="#94a3b8" />
            <YAxis stroke="#22c55e" tickFormatter={value => `${value} KAS`} />
            <Tooltip
              contentStyle={{backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0'}}
              labelStyle={{ color: '#94a3b8' }}
              formatter={value => [`${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} KAS`, 'Added']}
            />
            <Bar dataKey="added" fill="#22c55e" />
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
    )}

    {/* Mining Wallet Balance History (USDC) */}
    {balanceHistory.length > 0 && (
      <div className="box mt-5" style={styles.chartBox}>
        <h2 className="title is-5 mb-4" style={styles.tableTitle}>Mining Wallet Balance History (USDC)</h2>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={balanceHistory.slice(-30).map(item => {
              const d = new Date(item.date);
              const day = String(d.getDate()).padStart(2, '0');
              const month = d.toLocaleString('en-GB', { month: 'short' });
              return {
                ...item,
                dateLabel: `${day}-${month}`
              };
            })}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="dateLabel" stroke="#94a3b8" tickFormatter={value => value} />
            <YAxis stroke="#94a3b8" tickFormatter={value => `${value} USDC`} />
            <Tooltip
              contentStyle={{backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0'}}
              labelStyle={{ color: '#94a3b8' }}
              formatter={value => [`${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} USDC`, 'Balance']}
              labelFormatter={label => label}
            />
            <Line
              type="monotone"
              dataKey="usdcBalance"
              stroke="#60a5fa"
              strokeWidth={1}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
  );
};

export default DetailsTab;
